const getVariableSchema = ({
  sortedSiteVars,
  units,
  variableSources,
  DEFAULT_AGGREGATION_OPTIONS,
}) => ({
  maxItems: 10,
  type: 'array',
  title: 'Signals',
  items: {
    type: 'object',
    required: ['id', 'aggregate'],
    properties: {
      id: {
        type: 'string',
        title: 'Signal',
        default: sortedSiteVars?.[0]?.id,
        placeholder: 'Select signal',
        noOptionsMessage: 'No signals available',
        // use anyOf here even though not multi select because of issues with oneOf when many options
        anyOf: sortedSiteVars?.length
          ? sortedSiteVars.map((v) => ({
              enum: [v.id],
              title: `${v.stateset ? v.stateset.name : ''} [ ${
                variableSources.find((s) => s.id === v.source_id).name || '-'
              } ] [ ${v.itemDesignation || '-'} ] ${v.name}`,
            }))
          : [],
      },
      label: {
        type: 'string',
        title: 'Label',
      },
      unit: {
        type: 'string',
        title: 'Unit',
        default: units[0].id,
        oneOf: units.map((unit) => ({
          enum: [unit.id],
          title: `${unit.category}: ${unit.name}`,
        })),
      },
      unitLabel: {
        type: 'string',
        title: 'Custom Unit Label',
        description: 'Custom label to display instead of the unit name',
      },
      decimals: {
        type: 'string',
        title: 'Decimals',
        default: 'auto',
        enum: ['auto', '0', '1', '2', '3', '4', '5'],
      },
    },
    dependencies: {
      // https://github.com/rjsf-team/react-jsonschema-form/issues/1703
      id: {
        oneOf: variableSources.map((source) => {
          const { variables = [null], aggregationOptions, aggregationOptionNames } = source;

          const fillOptions = [
            { enum: ['null'], title: 'Show gaps (linear interpolation)' },
            {
              enum: ['null-monotone'],
              title: 'Show gaps (polynomial interpolation)',
            },
            {
              enum: ['connect'],
              title: 'Connect data points (linear interpolation)',
            },
            {
              enum: ['connect-monotone'],
              title: 'Connect data points (polynomial interpolation)',
            },
          ];
          if (source.type === 'dec') {
            fillOptions.push(
              {
                enum: ['previous'],
                title: 'Fill missing data points using previous value (linear interpolation)',
              },
              {
                enum: ['previous-monotone'],
                title: 'Fill missing data points using previous value (polynomial interpolation)',
              }
            );
          }

          let aggOptions = DEFAULT_AGGREGATION_OPTIONS.map(({ value, label }) => ({
            enum: [value],
            title: label,
          }));

          if (aggregationOptions && aggregationOptionNames) {
            aggOptions = aggregationOptions.map((agg, i) => ({
              enum: [agg],
              title: aggregationOptionNames[i],
            }));
          } else if (source.options?.aggregationOptions) {
            aggOptions = aggregationOptions.map((agg) => ({
              enum: [agg],
              title: agg.charAt(0).toUpperCase() + agg.slice(1),
            }));
          }

          const sourceOptions = {
            properties: {
              id: { enum: variables },
              aggregate: {
                type: 'string',
                title: 'Aggregate',
                default: 'max',
                oneOf: aggOptions,
              },
              fill: {
                title: 'Fill',
                description: 'How to fill any gaps in the series data',
                type: 'string',
                default: 'null',
                oneOf: fillOptions,
              },
            },
          };

          if (source.type === 'dummy') {
            sourceOptions.properties.quality = {
              title: 'Quality',
              description: 'Set data quality',
              type: 'string',
              default: '1',
              oneOf: [
                { enum: ['1'], title: 'Valid' },
                { enum: ['2'], title: 'Invalid' },
                { enum: ['random'], title: 'Random' },
              ],
            };
          }

          return sourceOptions;
        }),
      },
    },
  },
});

const updateVisualizationSchema = ({ visualization, siteVariables, schema, colors }) => {
  const baseSchema = schema(visualization.type);

  // Clone the schema to avoid mutating the original shared object
  const clonedSchema = {
    ...baseSchema,
    properties: { ...baseSchema.properties },
  };

  const varsWithDefinedAgg = (visualization.variables || [])
    .filter((v) => v.aggregate)
    .map((v) => {
      const siteVar = siteVariables.find((sv) => sv.id === v.id);
      if (!siteVar) {
        return null;
      }
      const { name: varName, itemDesignation } = siteVar;
      return {
        ...v,
        seriesLabel: `[ ${itemDesignation || '-'} ] ${v.label || varName} (${v.aggregate})`,
      };
    })
    .filter(Boolean);

  switch (visualization.type) {
    case 'duval':
    case 'duvalPentagon': {
      delete clonedSchema.properties.plots;
      break;
    }
    case 'star': {
      delete clonedSchema.properties.plot;
      break;
    }
    default: {
      break;
    }
  }

  if (visualization.type === 'graph' && varsWithDefinedAgg.length > 0) {
    baseSchema.properties.series = {
      type: 'array',
      title: 'Series',
      items: {
        type: 'object',
        properties: {
          axis: {
            type: 'string',
            title: 'Y-axis position',
            default: 'left',
            enum: ['left', 'right'],
          },
          id: {
            type: 'string',
            title: 'Signal id',
            default: `${varsWithDefinedAgg[0].id}.${varsWithDefinedAgg[0].aggregate}`,
            oneOf: varsWithDefinedAgg.map((variable) => ({
              enum: [`${variable.id}.${variable.aggregate}`],
              title: variable.seriesLabel,
            })),
          },
          color: {
            type: 'string',
            title: 'Color',
            default: colors[0].value,
            oneOf: colors.map((color) => ({
              enum: [color.value],
              title: color.label,
            })),
          },
        },
      },
    };
  }

  if (visualization.type === 'duval' && varsWithDefinedAgg.length) {
    const duvalSignalIdSchema = {
      type: 'string',
      default: `${varsWithDefinedAgg[0].id}.${varsWithDefinedAgg[0].aggregate}`,
      oneOf: varsWithDefinedAgg.map((v) => ({
        enum: [`${v.id}.${v.aggregate}`],
        title: v.seriesLabel,
      })),
    };
    clonedSchema.properties.plots = {
      maxItems: 3,
      type: 'array',
      title: 'Plots',
      items: {
        type: 'object',
        title: 'Configure plot',
        properties: {
          label: {
            type: 'string',
            title: 'Label',
          },
          color: {
            type: 'string',
            title: 'Color',
            default: colors[0].value,
            oneOf: colors.map((color) => ({
              enum: [color.value],
              title: color.label,
            })),
          },
          rollingAverageColor: {
            type: 'string',
            title: 'Rolling average color',
            default: colors[1].value,
            oneOf: colors.map((color) => ({
              enum: [color.value],
              title: color.label,
            })),
          },
          leftId: {
            title: 'Left axis signal',
            ...duvalSignalIdSchema,
          },
          rightId: {
            title: 'Right axis signal',
            ...duvalSignalIdSchema,
          },
          bottomId: {
            title: 'Bottom axis signal',
            ...duvalSignalIdSchema,
          },
        },
      },
    };
  }

  if (visualization.type === 'duvalPentagon' && varsWithDefinedAgg.length) {
    const duvalSignalIdSchema = {
      type: 'string',
      default: `${varsWithDefinedAgg[0].id}.${varsWithDefinedAgg[0].aggregate}`,
      oneOf: varsWithDefinedAgg.map((v) => ({
        enum: [`${v.id}.${v.aggregate}`],
        title: v.seriesLabel,
      })),
    };
    clonedSchema.properties.plots = {
      maxItems: 3,
      type: 'array',
      title: 'Plots',
      items: {
        type: 'object',
        title: 'Configure plot',
        properties: {
          label: {
            type: 'string',
            title: 'Label',
          },
          color: {
            type: 'string',
            title: 'Color',
            default: colors[0].value,
            oneOf: colors.map((color) => ({
              enum: [color.value],
              title: color.label,
            })),
          },
          rollingAverageColor: {
            type: 'string',
            title: 'Rolling average color',
            default: colors[1].value,
            oneOf: colors.map((color) => ({
              enum: [color.value],
              title: color.label,
            })),
          },
          h2Id: {
            title: 'H2 axis signal',
            ...duvalSignalIdSchema,
          },
          c2h6Id: {
            title: 'C2H6 axis signal',
            ...duvalSignalIdSchema,
          },
          ch4Id: {
            title: 'CH4 axis signal',
            ...duvalSignalIdSchema,
          },
          c2h4Id: {
            title: 'C2H4 axis signal',
            ...duvalSignalIdSchema,
          },
          c2h2Id: {
            title: 'C2H2 axis signal',
            ...duvalSignalIdSchema,
          },
        },
      },
    };
  }

  if (visualization.type === 'star' && varsWithDefinedAgg.length) {
    const starSignalIdSchema = {
      type: 'string',
      default: `${varsWithDefinedAgg[0].id}.${varsWithDefinedAgg[0].aggregate}`,
      oneOf: varsWithDefinedAgg.map((v) => ({
        enum: [`${v.id}.${v.aggregate}`],
        title: v.seriesLabel,
      })),
    };
    clonedSchema.properties.plot = {
      type: 'object',
      title: 'Assign signals to plot vectors',
      properties: {
        label: {
          type: 'string',
          title: 'Label',
        },
        L1: {
          title: 'Vector 1 signal id',
          ...starSignalIdSchema,
        },
        L2: {
          title: 'Vector 2 signal id',
          ...starSignalIdSchema,
        },
        L3: {
          title: 'Vector 3 signal id',
          ...starSignalIdSchema,
        },
        color: {
          type: 'string',
          title: 'Color',
          default: colors[0].value,
          oneOf: colors.map((color) => ({
            enum: [color.value],
            title: color.label,
          })),
        },
      },
    };
  }

  return clonedSchema;
};

export { getVariableSchema, updateVisualizationSchema };
